import { isLocalFirstYjsEnabled } from "./local-first-settings";

const ACTIVE_LOCAL_DB_NAME = "illumi-local";
export const STALE_IDB_CLEANUP_SESSION_KEY =
	"illumi-local:stale-idb-cleaned-v1";

const LEGACY_PER_DOC_DB_PATTERN =
	/^(Whiteboard|Card|Preprint|Draft|Threads)-[A-Za-z0-9_-]+(?::\d+)*$/;

const LEGACY_CACHE_DB_PATTERNS = [
	/^illumi-yjs-cache$/,
	/^illumi_blocknote_cache(_v\d+)?$/,
	/^illumi_board_cache(_v\d+)?$/,
];

let cleanupScheduled = false;

/**
 * Returns true if the given IndexedDB database name belongs to a deprecated
 * per-document `y-indexeddb` database (`Whiteboard-*`, `Card-*`, etc.) or an
 * older cache version (`illumi-yjs-cache`, `illumi_blocknote_cache_v1`,
 * `illumi_board_cache_v1`), and is never the active `illumi-local` database.
 */
export function isStaleIllumiDatabaseName(name: string): boolean {
	if (!name || name === ACTIVE_LOCAL_DB_NAME) {
		return false;
	}
	if (LEGACY_PER_DOC_DB_PATTERN.test(name)) {
		return true;
	}
	return LEGACY_CACHE_DB_PATTERNS.some((pattern) => pattern.test(name));
}

function scheduleLowPriorityTask(task: () => void, delayMs = 1500): void {
	if (
		typeof window !== "undefined" &&
		typeof window.requestIdleCallback === "function"
	) {
		window.requestIdleCallback(() => task(), { timeout: delayMs * 2 });
		return;
	}
	setTimeout(task, delayMs);
}

function deleteDatabaseQuietly(name: string): Promise<void> {
	if (typeof indexedDB === "undefined" || !indexedDB?.deleteDatabase) {
		return Promise.resolve();
	}
	return new Promise<void>((resolve) => {
		try {
			const req = indexedDB.deleteDatabase(name);
			req.onsuccess = () => resolve();
			req.onerror = () => resolve();
			req.onblocked = () => resolve();
		} catch {
			resolve();
		}
	});
}

function hasSessionCleanupCompleted(): boolean {
	try {
		return (
			typeof sessionStorage !== "undefined" &&
			sessionStorage.getItem(STALE_IDB_CLEANUP_SESSION_KEY) === "1"
		);
	} catch {
		return false;
	}
}

function markSessionCleanupCompleted(): void {
	try {
		if (typeof sessionStorage !== "undefined") {
			sessionStorage.setItem(STALE_IDB_CLEANUP_SESSION_KEY, "1");
		}
	} catch {
		// Ignore storage quota or privacy mode errors
	}
}

/**
 * Enumerates browser IndexedDB databases via `indexedDB.databases()` and
 * deletes stale per-document and legacy-versioned databases one by one across
 * idle slices so active `illumi-local` transactions are never starved.
 *
 * Only runs when Experimental Local-First Yjs Sync (`isLocalFirstYjsEnabled()`)
 * is enabled.
 */
export async function runStaleIndexedDbCleanup(options?: {
	yieldBetweenDeletes?: boolean;
}): Promise<string[]> {
	if (!isLocalFirstYjsEnabled()) {
		return [];
	}
	if (
		typeof indexedDB === "undefined" ||
		typeof indexedDB.databases !== "function"
	) {
		return [];
	}

	let databases: IDBDatabaseInfo[];
	try {
		databases = await indexedDB.databases();
	} catch {
		return [];
	}

	const staleNames = databases
		.map((info) => info.name)
		.filter((name): name is string =>
			Boolean(name && isStaleIllumiDatabaseName(name)),
		);

	if (staleNames.length === 0) {
		markSessionCleanupCompleted();
		return [];
	}

	const shouldYield = options?.yieldBetweenDeletes ?? true;
	const deleted: string[] = [];

	for (const name of staleNames) {
		if (!isLocalFirstYjsEnabled()) {
			return deleted;
		}
		if (shouldYield) {
			await new Promise<void>((resolve) =>
				scheduleLowPriorityTask(resolve, 150),
			);
			if (!isLocalFirstYjsEnabled()) {
				return deleted;
			}
		}
		await deleteDatabaseQuietly(name);
		deleted.push(name);
	}

	markSessionCleanupCompleted();
	return deleted;
}

/**
 * Schedules a one-time, low-priority background sweep for stale IndexedDB
 * databases after `illumi-local` opens, gated on `isLocalFirstYjsEnabled()`.
 */
export function scheduleStaleIndexedDbCleanup(): void {
	if (!isLocalFirstYjsEnabled()) {
		return;
	}
	if (cleanupScheduled || hasSessionCleanupCompleted()) {
		return;
	}
	if (
		typeof indexedDB === "undefined" ||
		typeof indexedDB.databases !== "function"
	) {
		return;
	}
	cleanupScheduled = true;
	scheduleLowPriorityTask(() => {
		void runStaleIndexedDbCleanup({ yieldBetweenDeletes: true });
	}, 2000);
}

export function resetStaleIndexedDbCleanupStateForTests(): void {
	cleanupScheduled = false;
	try {
		if (typeof sessionStorage !== "undefined") {
			sessionStorage.removeItem(STALE_IDB_CLEANUP_SESSION_KEY);
		}
	} catch {
		// Ignore
	}
}
